/**
 * DeviceSettingsModal: 설정 다이얼로그 (v1 SettingsDialog 6탭 구조 기반).
 * 원본: wehago-conference RenewalComponents/Footer/SettingsDialog
 *
 * 탭: 비디오 | 오디오 | 설정
 * (정보/배경/회의자료 탭은 별도 컴포넌트로 분리됨)
 */
import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';
import { localActions } from '@/store/slices/localSlice';

interface DeviceSettingsModalProps {
  onChangeDevice?: (type: string, deviceId: string) => void;
}

// ─── 디바이스 선택 드롭다운 ──────────────────────────────────────────────────

interface DeviceSelectProps {
  title: string;
  type: 'videoinput' | 'audioinput' | 'audiooutput';
  devices: MediaDeviceInfo[];
  selectedId: string;
  onChange: (type: string, deviceId: string) => void;
}

const DeviceSelect: React.FC<DeviceSelectProps> = ({ title, type, devices, selectedId, onChange }) => {
  const [open, setOpen] = useState(false);
  const selected = devices.find((d) => d.deviceId === selectedId);
  const label = selected?.label ?? (devices.length === 0 ? '디바이스 없음' : '선택');

  return (
    <div className="rtcDialogSetupNew__item">
      <strong className="rtcDialogSetupNew__itemTitle">{title}</strong>
      <div className={`LUX_basic_select LUX_renewal${open ? ' on' : ''} rtcCustoms_deviceSelect`}>
        <div className="searchbx rtcCustoms_deviceSelect__trigger" onClick={() => setOpen((v) => !v)}>
          <span className="inpbx" title={label}>
            <span className="placeholder text_black">{label}</span>
          </span>
          <button type="button" className="btn">
            <span className="sp_lux">선택</span>
          </button>
        </div>
        {open && devices.length > 0 && (
          <div className="resultbx">
            <div className="result_scrall rtcCustoms_deviceSelect__list">
              <div className="result_scrallin">
                <ul className="result_lst">
                  {devices.map((d) => (
                    <li key={d.deviceId} title={d.label}>
                      <a
                        href="#"
                        className={d.deviceId === selectedId ? 'on' : ''}
                        onClick={(e) => { e.preventDefault(); onChange(type, d.deviceId); setOpen(false); }}
                      >
                        <div>{d.label || '알 수 없는 디바이스'}</div>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── 토글 행 ──────────────────────────────────────────────────────────────────

interface ToggleRowProps {
  label: string;
  value: boolean;
  onText?: string;
  offText?: string;
  onChange: (v: boolean) => void;
}

const ToggleRow: React.FC<ToggleRowProps> = ({ label, value, onText = '사용', offText = '미사용', onChange }) => (
  <div className="rtcDialogSetupNew__item">
    <strong className="rtcDialogSetupNew__itemTitle">{label}</strong>
    <div className="rtcDialogSetupNew__toggleWrap">
      <span>{value ? onText : offText}</span>
      <div
        className="btn_onoff rtcCustoms_deviceToggle"
        onClick={() => onChange(!value)}
      >
        <label onClick={(e) => e.preventDefault()}>
          <input type="checkbox" readOnly checked={value} />
          <span className="toggle" />
        </label>
      </div>
    </div>
  </div>
);

// ─── 탭 메뉴 아이템 ──────────────────────────────────────────────────────────

const menuItems = [
  { label: '비디오', key: 'video' },
  { label: '오디오', key: 'audio' },
  { label: '설정', key: 'settings' },
] as const;

type TabKey = typeof menuItems[number]['key'];

// ─── 메인 모달 ────────────────────────────────────────────────────────────────

const DeviceSettingsModal: React.FC<DeviceSettingsModalProps> = ({ onChangeDevice }) => {
  const dispatch = useAppDispatch();
  const ui = useAppSelector((s) => s.ui);
  const local = useAppSelector((s) => s.local);

  const [selectedTab, setSelectedTab] = useState<TabKey>('video');
  const [pendingCameraId, setPendingCameraId] = useState(local.videoInputDeviceId);
  const [pendingMicId, setPendingMicId] = useState(local.audioInputDeviceId);
  const [pendingSpeakerId, setPendingSpeakerId] = useState(local.audioOutputDeviceId);
  const [autoSpeaker, setAutoSpeaker] = useState(false);
  const [speechText, setSpeechText] = useState(false);

  // 모달 열릴 때 디바이스 목록 갱신
  useEffect(() => {
    if (!ui.isSettingsModalOpen) return;
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      dispatch(localActions.setDevices({
        audioInputDevices: devices.filter((d) => d.kind === 'audioinput') as MediaDeviceInfo[],
        videoInputDevices: devices.filter((d) => d.kind === 'videoinput') as MediaDeviceInfo[],
        audioOutputDevices: devices.filter((d) => d.kind === 'audiooutput') as MediaDeviceInfo[],
      }));
    }).catch(() => {});
  }, [ui.isSettingsModalOpen]);

  if (!ui.isSettingsModalOpen) return null;

  const handleClose = () => dispatch(uiActions.setSettingsModalOpen(false));

  const handleChangeDevice = (type: string, deviceId: string) => {
    if (type === 'videoinput') setPendingCameraId(deviceId);
    else if (type === 'audioinput') setPendingMicId(deviceId);
    else if (type === 'audiooutput') setPendingSpeakerId(deviceId);

    // 실제 트랙/출력 장치 교체는 상위 핸들러에서 적용
    onChangeDevice?.(type, deviceId);
  };

  const renderContent = () => {
    switch (selectedTab) {
      case 'video':
        return (
          <DeviceSelect
            title="카메라"
            type="videoinput"
            devices={local.videoInputDevices}
            selectedId={pendingCameraId}
            onChange={handleChangeDevice}
          />
        );
      case 'audio':
        return (
          <>
            <DeviceSelect
              title="마이크"
              type="audioinput"
              devices={local.audioInputDevices}
              selectedId={pendingMicId}
              onChange={handleChangeDevice}
            />
            <DeviceSelect
              title="스피커"
              type="audiooutput"
              devices={local.audioOutputDevices}
              selectedId={pendingSpeakerId}
              onChange={handleChangeDevice}
            />
          </>
        );
      case 'settings':
        return (
          <>
            <ToggleRow
              label="발표자 자동전환"
              value={autoSpeaker}
              onChange={setAutoSpeaker}
            />
            <ToggleRow
              label="음성인식"
              value={speechText}
              onChange={setSpeechText}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* 바깥 클릭 닫기 */}
      <div
        className="rtcCustoms_deviceModal__backdrop"
        onClick={handleClose}
      />

      {/* 다이얼로그 */}
      <div
        className="dialog_content rtc_dialog rtc_new4_dialog roundstyle rtcCustoms_deviceModal__dialog"
      >
        <div className="dialog_data">
          {/* 타이틀 */}
          <div className="dialog_data_tit">
            <h1 className="txtcnt">설정</h1>
            <button type="button" className="LUX_basic_btn btn_clr" onClick={handleClose}>
              <span className="sp_lux">닫기</span>
            </button>
          </div>

          {/* 콘텐츠 영역 */}
          <div className="dialog_data_area rtcDialogSetup_dataArea">
            <div className="rtcDialogSetupNew rtcCustoms_deviceModal__layout">
              {/* 좌측 메뉴 */}
              <div className="rtcDialogSetupNew__left rtcCustoms_deviceModal__leftMenu">
                <ul className="rtcDialogSetupNew__list">
                  {menuItems.map((item) => (
                    <li key={item.key}>
                      <button
                        type="button"
                        className={`rtcDialogSetupNew__listBtn rtcCustoms_deviceModal__tabBtn${selectedTab === item.key ? ' rtcCustoms_deviceModal__tabBtn--active selected' : ' rtcCustoms_deviceModal__tabBtn--inactive'}`}
                        onClick={() => setSelectedTab(item.key)}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* 우측 콘텐츠 */}
              <div className="rtcDialogSetupNew__right rtcCustoms_deviceModal__rightContent">
                {renderContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeviceSettingsModal;
